// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";

// Custom packages
import { finalize } from "rxjs/operators";
import { AuthService } from "src/app/modules/auth/auth.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { PropertyInterface } from "src/app/shared/models/property/property.interface";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

/**
 * Script start
 */
@Component({
  selector: "app-nfc-detail-dialog",
  templateUrl: "./nfc-detail-dialog.component.html",
  styleUrls: ["./nfc-detail-dialog.component.scss"],
})
export class NfcDetailDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = false; // Used to view/hide form loading spinner

  // BEGIN - Edit item form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  property: PropertyInterface;
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  // END - Edit item form

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<NfcDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private propertiesService: PropertiesService
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    if (this.data && this.data.property) {
      this.property = this.data.property;
    }

    // Init form
    this.initForm();
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = this.property.nfcDetails;

    // Set fields
    this.fields = [
      ...this.fields,
      {
        fieldGroupClassName: "row",
        fieldGroup: [
          {
            className: "col-12",
            type: "input",
            key: "tag",
            templateOptions: {
              label: this.translateService.instant("nfcAccessLogs.detail.tag"),
              placeholder: this.translateService.instant(
                "nfcAccessLogs.detail.tag"
              ),
              required: true,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: "col-12",
            type: "input",
            key: "tag2",
            templateOptions: {
              label: this.translateService.instant("nfcAccessLogs.detail.tag2"),
              placeholder: this.translateService.instant(
                "nfcAccessLogs.detail.tag2"
              ),
              required: false,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: "col-12",
            type: "input",
            key: "tag3",
            templateOptions: {
              label: this.translateService.instant("nfcAccessLogs.detail.tag3"),
              placeholder: this.translateService.instant(
                "nfcAccessLogs.detail.tag3"
              ),
              required: false,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: "col-12",
            type: "input",
            key: "tag4",
            templateOptions: {
              label: this.translateService.instant("nfcAccessLogs.detail.tag4"),
              placeholder: this.translateService.instant(
                "nfcAccessLogs.detail.tag4"
              ),
              required: false,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: "col-12",
            type: "input",
            key: "tag5",
            templateOptions: {
              label: this.translateService.instant("nfcAccessLogs.detail.tag5"),
              placeholder: this.translateService.instant(
                "nfcAccessLogs.detail.tag5"
              ),
              required: false,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: "col-12",
            type: "input",
            key: "tag6",
            templateOptions: {
              label: this.translateService.instant("nfcAccessLogs.detail.tag6"),
              placeholder: this.translateService.instant(
                "nfcAccessLogs.detail.tag6"
              ),
              required: false,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: "col-12",
            type: "input",
            key: "tag7",
            templateOptions: {
              label: this.translateService.instant("nfcAccessLogs.detail.tag7"),
              placeholder: this.translateService.instant(
                "nfcAccessLogs.detail.tag7"
              ),
              required: false,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: "col-12",
            type: "input",
            key: "tag8",
            templateOptions: {
              label: this.translateService.instant("nfcAccessLogs.detail.tag8"),
              placeholder: this.translateService.instant(
                "nfcAccessLogs.detail.tag8"
              ),
              required: false,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: "col-12",
            type: "input",
            key: "tag9",
            templateOptions: {
              label: this.translateService.instant("nfcAccessLogs.detail.tag9"),
              placeholder: this.translateService.instant(
                "nfcAccessLogs.detail.tag9"
              ),
              required: false,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: "col-12",
            type: "input",
            key: "tag10",
            templateOptions: {
              label: this.translateService.instant(
                "nfcAccessLogs.detail.tag10"
              ),
              placeholder: this.translateService.instant(
                "nfcAccessLogs.detail.tag10"
              ),
              required: false,
              minLength: 2,
              maxLength: 50,
            },
          },
          {
            className: "col-12",
            type: "textarea",
            key: "message",
            templateOptions: {
              label: this.translateService.instant(
                "nfcAccessLogs.detail.message"
              ),
              placeholder: this.translateService.instant(
                "nfcAccessLogs.detail.message"
              ),
              required: false,
              maxLength: 5000,
              rows: 4,
            },
            expressionProperties: {
              "templateOptions.message":
                '(model[field.key]||"").length+"/" +field.templateOptions.maxLength',
            },
          },
        ],
      },
    ];
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsTouched();
      });
      console.warn("form", this.form);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid"),
        this.translateService.instant("common.words.error")
      );
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;
    if (this.model) {
      if (this.model.tag) {
        let tag = this.model.tag;
        tag = tag.replace(/[^0-9a-fA-F]/gi, "").toUpperCase();
        this.model.tag = tag;
      }
      if (this.model.tag2) {
        let tag = this.model.tag2;
        tag = tag.replace(/[^0-9a-fA-F]/gi, "").toUpperCase();
        this.model.tag2 = tag;
      }
      if (this.model.tag3) {
        let tag = this.model.tag3;
        tag = tag.replace(/[^0-9a-fA-F]/gi, "").toUpperCase();
        this.model.tag3 = tag;
      }
      if (this.model.tag4) {
        let tag = this.model.tag4;
        tag = tag.replace(/[^0-9a-fA-F]/gi, "").toUpperCase();
        this.model.tag4 = tag;
      }
      if (this.model.tag5) {
        let tag = this.model.tag5;
        tag = tag.replace(/[^0-9a-fA-F]/gi, "").toUpperCase();
        this.model.tag5 = tag;
      }
      if (this.model.tag6) {
        let tag = this.model.tag6;
        tag = tag.replace(/[^0-9a-fA-F]/gi, "").toUpperCase();
        this.model.tag6 = tag;
      }
      if (this.model.tag7) {
        let tag = this.model.tag7;
        tag = tag.replace(/[^0-9a-fA-F]/gi, "").toUpperCase();
        this.model.tag7 = tag;
      }
      if (this.model.tag8) {
        let tag = this.model.tag8;
        tag = tag.replace(/[^0-9a-fA-F]/gi, "").toUpperCase();
        this.model.tag8 = tag;
      }
      if (this.model.tag9) {
        let tag = this.model.tag9;
        tag = tag.replace(/[^0-9a-fA-F]/gi, "").toUpperCase();
        this.model.tag9 = tag;
      }
      if (this.model.tag10) {
        let tag = this.model.tag10;
        tag = tag.replace(/[^0-9a-fA-F]/gi, "").toUpperCase();
        this.model.tag10 = tag;
      }
    }

    this.subscriptions.push(
      this.propertiesService
        .updateNfcDetails(this.property._id, this.model)
        .pipe(finalize(() => (this.formBtnOptions.active = false)))
        .subscribe(
          (res) => {
            if (res.status) {
              this.toastrService.success(res.message);
              this.dialogRef.close();
            }
          },
          (err) => {
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }
}
